import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { userManagementApi } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, User as UserIcon, Mail, Building2, Shield,
  ShieldCheck, Clock, UserCog, Key, Edit2,
} from 'lucide-react';
import RelatedLists from '../../components/RelatedLists';

const ROLE_LABELS = {
  super_admin: 'Super Admin', practice_admin: 'Practice Admin', physician: 'Physician',
  nurse: 'Nurse', care_coordinator: 'Care Coordinator', aide: 'Aide (Home Care)',
  billing_specialist: 'Billing Specialist', patient: 'Patient', csm_agent: 'CSM Agent',
  director_patient_services: 'Director of Patient Services',
  service_provider: 'Service Provider',
};
const ROLE_COLORS = {
  super_admin: '#7F1D1D', practice_admin: '#1A56DB', physician: '#0E7490',
  nurse: '#166534', care_coordinator: '#0E7490', aide: '#9A3412',
  billing_specialist: '#92400E', patient: '#64748B', csm_agent: '#7C3AED',
  director_patient_services: '#7C3AED',
  service_provider: '#0891B2',
};

function formatDate(ts) {
  if (!ts) return 'Never';
  try { return new Date(ts).toLocaleString(); } catch { return '—'; }
}

export default function AdminUserDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await userManagementApi.get(id);
      setUser(r.data);
      if (r.data.organization_id) {
        try {
          const orgs = await api.get('/organizations');
          const list = Array.isArray(orgs.data) ? orgs.data : (orgs.data.organizations || []);
          const found = list.find(o => o.id === r.data.organization_id);
          setOrgName(found?.name || '');
        } catch { /* ignore */ }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load user');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleImpersonate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/impersonate/${id}`);
      localStorage.setItem('savenes_token', r.data.access_token);
      localStorage.setItem('savenes_original_admin', r.data.original_admin_id);
      localStorage.setItem('savenes_impersonating', r.data.impersonating);
      toast.success(`Now impersonating ${r.data.impersonating}`);
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to impersonate');
    } finally { setBusy(false); }
  };

  const handleResetPassword = async () => {
    if (!window.confirm(`Reset password for ${user.email}? A temporary password will be shown.`)) return;
    setBusy(true);
    try {
      const r = await userManagementApi.resetPassword(id);
      toast.success(`Temporary password: ${r.data.temporary_password}`, { duration: 12000 });
    } catch { toast.error('Failed to reset password'); }
    finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-[#64748B]" data-testid="user-detail-loading">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading user…
      </div>
    );
  }
  if (!user) return <div className="p-12 text-center text-[#64748B]">User not found</div>;

  const initials = (user.name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase();
  const roleColor = ROLE_COLORS[user.role] || '#64748B';

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-user-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/admin/users')}
          className="inline-flex items-center text-sm text-[#0E7490] hover:underline"
          data-testid="back-to-users-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to users
        </button>
        <div className="flex items-center gap-2">
          {user.role !== 'super_admin' && (
            <Button
              size="sm" variant="outline" onClick={handleImpersonate} disabled={busy}
              data-testid="impersonate-btn"
            >
              <UserCog className="w-4 h-4 mr-1" /> Impersonate
            </Button>
          )}
          <Button
            size="sm" variant="outline" onClick={handleResetPassword} disabled={busy}
            data-testid="reset-pwd-btn"
          >
            <Key className="w-4 h-4 mr-1" /> Reset password
          </Button>
          <Link to="/admin/users">
            <Button size="sm" variant="ghost" data-testid="edit-from-detail-btn">
              <Edit2 className="w-4 h-4 mr-1" /> Edit
            </Button>
          </Link>
        </div>
      </div>

      {/* Identity card */}
      <Card className="border-[#E2E8F0]" data-testid="user-identity-card">
        <CardContent className="p-6">
          <div className="flex items-start gap-5">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl font-semibold flex-shrink-0"
              style={{ backgroundColor: roleColor }}
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-[#0A2342]" data-testid="user-name">{user.name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: roleColor + '15', color: roleColor }}
                  data-testid="user-role-badge"
                >
                  {ROLE_LABELS[user.role] || user.role}
                </span>
                {user.is_active !== false
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Active</Badge>
                  : <Badge className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">Inactive</Badge>}
                {user.mfa_enabled
                  ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]"><ShieldCheck className="w-3 h-3 mr-0.5 inline" />MFA on</Badge>
                  : <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]"><Shield className="w-3 h-3 mr-0.5 inline" />MFA off</Badge>}
              </div>
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-4 text-sm">
                <Field icon={<Mail className="w-3.5 h-3.5" />} label="Email" value={user.email} />
                <Field icon={<Building2 className="w-3.5 h-3.5" />} label="Organization" value={orgName || '—'} />
                <Field icon={<UserIcon className="w-3.5 h-3.5" />} label="User ID" value={user.id} mono />
                <Field icon={<Clock className="w-3.5 h-3.5" />} label="Last login" value={formatDate(user.last_login)} />
                <Field icon={<Clock className="w-3.5 h-3.5" />} label="Created" value={formatDate(user.created_at)} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Related Lists block */}
      <RelatedLists scope="user" id={id} />
    </div>
  );
}

function Field({ icon, label, value, mono }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-[#94A3B8] mt-0.5">{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-[#94A3B8]">{label}</div>
        <div className={`text-sm text-[#0A2342] truncate ${mono ? 'font-mono text-xs' : ''}`}>{value || '—'}</div>
      </div>
    </div>
  );
}
